import { useEffect, useState } from "react";

import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { getAccessToken } from "./token";
import { API_URL } from "../enums/api";
import { Optional } from "../enums/types";

export type EntityMediaType = "image" | "video" | "audio";

export type MediaData = {
  url: string;
  type: EntityMediaType;
  thumbnailUrl?: string;
  key?: string;
  cookies?: Array<{
    name: string;
    value: string;
    path?: string;
    domain?: string;
    maxAge?: number;
    secure?: boolean;
  }>;
};

type CookieResponse = {
  domain?: string;
  httpOnly?: boolean;
  maxAge?: number;
  name?: string;
  path?: string;
  secure?: boolean;
  value?: string;
};

type EntityMediaResponse = {
  audio?: {
    content?: {
      cookies?: CookieResponse[];
      url?: string;
    };
    id?: string;
  };
  createdAt?: string;
  id?: string;
  image?: {
    content?: Array<{
      type?: string;
      url?: string;
    }>;
    id?: string;
  };
  key?: string;
  type?: EntityMediaType;
  updatedAt?: string;
  video?: {
    content?: {
      cookies?: CookieResponse[];
      thumbnail?: Array<{
        type?: string;
        url?: string;
      }>;
      url?: string;
    };
    id?: string;
  };
};

const useGetMedia = (mediaId?: string) => {
  const { t } = useTranslation();
  const [isMediaDownloading, setIsMediaDownloading] = useState<boolean>(false);
  const [downloadedMedia, setDownloadedMedia] =
    useState<Optional<MediaData>>(null);

  useEffect(
    function fetchMedia() {
      if (mediaId) {
        setIsMediaDownloading(true);

        axios
          .get<EntityMediaResponse>(`${API_URL}bo/media/${mediaId}`, {
            headers: {
              Authorization: `Bearer ${getAccessToken()}`,
            },
          })
          .then(({ data }) => {
            let mediaData: Optional<MediaData> = null;

            if (data.type === "image" && data.image?.content) {
              const imageUrl =
                data.image.content.find((img) => img.type === "square-large")
                  ?.url || data.image.content[0]?.url;
              if (imageUrl) {
                mediaData = {
                  url: imageUrl,
                  type: "image",
                  key: data.key,
                };
              }
            } else if (data.type === "video" && data.video?.content?.url) {
              const thumbnailUrl = data.video.content.thumbnail?.find(
                (thumb) => thumb.type === "square-large"
              )?.url;
              mediaData = {
                url: data.video.content.url,
                type: "video",
                thumbnailUrl,
                key: data.key,
                cookies: data.video.content.cookies?.map((c) => ({
                  name: c.name || "",
                  value: c.value || "",
                  path: c.path,
                  domain: c.domain,
                  maxAge: c.maxAge,
                  secure: c.secure,
                })),
              };
            } else if (data.type === "audio" && data.audio?.content?.url) {
              mediaData = {
                url: data.audio.content.url,
                type: "audio",
                key: data.key,
                cookies: data.audio.content.cookies?.map((c) => ({
                  name: c.name || "",
                  value: c.value || "",
                  path: c.path,
                  domain: c.domain,
                  maxAge: c.maxAge,
                  secure: c.secure,
                })),
              };
            }

            setDownloadedMedia(mediaData);
            setIsMediaDownloading(false);
          })
          .catch(() => {
            toast.warning(t("app.image.edit.failed"));
            setIsMediaDownloading(false);
          });
      } else {
        setDownloadedMedia(null);
      }
    },
    [mediaId, t]
  );

  return { isMediaDownloading, downloadedMedia };
};

export { useGetMedia };
